import { ChevronDown, FileText } from "lucide-react";
import { useMemo, useState } from "react";

import { useListTranscriptItemsForScribingSession } from "@elation/api-client/src/stitch-web";

import { TranscriptItemEntity } from "@src/features/transcription";
import { HorizontalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { cn } from "@src/shared/lib/utils";
import { STRING_ID_NOT_READY } from "@src/shared/utils/constants";
import { formatDurationMmSs } from "@src/shared/utils/dates";

const TRANSCRIPT_POLL_INTERVAL_MS = 3_000;

export interface VisitTranscriptItemV2 {
  id: string;
  startOffsetMs: number;
  endOffsetMs?: number;
  speaker?: string | null;
  speakerLabel?: string | null;
  state?: "not_sent" | "sent" | "processed" | "errored";
  text?: string | null;
}

export interface VisitTranscriptProps {
  defaultOpen?: boolean;
  encounterId?: number;
  scribingSessionId?: string | null;
}

export function VisitTranscript({
  defaultOpen = false,
  encounterId,
  scribingSessionId,
}: VisitTranscriptProps) {
  const [open, setOpen] = useState(defaultOpen);
  const transcriptItems = useListTranscriptItemsForScribingSession(
    encounterId ?? 0,
    scribingSessionId ?? STRING_ID_NOT_READY,
    {
      query: {
        enabled: encounterId != null && scribingSessionId != null,
        refetchInterval: TRANSCRIPT_POLL_INTERVAL_MS,
      },
    },
  );
  const items = useMemo<VisitTranscriptItemV2[]>(() => {
    const sorted = [...(transcriptItems.data?.data ?? [])].sort(
      TranscriptItemEntity.compareByOffsetAsc,
    );
    const speakerLabelMap =
      TranscriptItemEntity.buildSessionSpeakerLabelMap(sorted);

    return sorted.map((item) => ({
      id: String(item.id),
      startOffsetMs: item.startOffsetMs,
      endOffsetMs: item.endOffsetMs,
      speaker: item.speaker,
      speakerLabel: TranscriptItemEntity.sessionSpeakerLabel(
        item.speaker,
        speakerLabelMap,
      ),
      state: item.state,
      text: item.text,
    }));
  }, [transcriptItems.data?.data]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-t border-border py-2">
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="flex w-full items-center justify-start gap-2 text-left text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground"
            aria-label={open ? "Collapse transcript" : "Expand transcript"}
          >
            <ChevronDown
              className={cn(
                "size-3.5 transition-transform",
                !open && "-rotate-90",
              )}
              aria-hidden="true"
            />
            Transcript
            <span>({items.length} lines)</span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {items.length > 0 ? (
            <ol
              className="mt-2 flex max-h-32 flex-col gap-1.5 overflow-y-auto pr-2"
              aria-label="Transcript items"
            >
              {items.map((item) => (
                <TranscriptRow key={item.id} item={item} />
              ))}
            </ol>
          ) : (
            <HorizontalFlexbox
              alignItems="center"
              className="mt-3 gap-2 rounded-[9px] border border-dashed border-border px-3 py-4 text-[12px] text-muted-foreground"
            >
              <FileText className="size-4" aria-hidden="true" />
              No transcript yet. Start recording to capture the visit.
            </HorizontalFlexbox>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function TranscriptRow({ item }: { item: VisitTranscriptItemV2 }) {
  const isPending = item.state === "not_sent" || item.state === "sent";
  const isErrored = item.state === "errored";

  return (
    <li className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[12px] leading-relaxed">
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
        {formatDurationMmSs(item.startOffsetMs)}
      </span>
      <p
        className={cn(
          "min-w-0 text-foreground",
          isPending && "italic text-muted-foreground",
          isErrored && "text-destructive",
        )}
      >
        {item.speakerLabel ? (
          <span className="font-semibold text-primary">
            {item.speakerLabel}:{" "}
          </span>
        ) : null}
        {item.text?.trim() ? item.text : "(no transcription)"}
      </p>
    </li>
  );
}
